import React, { useMemo, useState } from "react";
import { Alert, Button, Empty, Popconfirm, Space, Table, Tag, Tooltip, Upload, message } from "antd";
import {
    DeleteOutlined,
    EditOutlined,
    ExclamationCircleOutlined,
    EyeOutlined,
    UploadOutlined,
} from "@ant-design/icons";

import {
    formatPlanAmount,
    formatPlanDate,
    invoiceOptions,
} from "./transactionPlanUi";
import { useTranslation } from "../../i18n/LanguageProvider";

function renderCompactText(value, maxWidth = 220) {
    const text = value || "-";

    return (
        <div
            title={text}
            style={{
                maxWidth,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
            }}
        >
            {text}
        </div>
    );
}

function InvoiceUploadButton({ record, onUpload, t }) {
    const [uploading, setUploading] = useState(false);

    const uploadProps = {
        accept: ".pdf,application/pdf",
        showUploadList: false,
        beforeUpload: async (file) => {
            setUploading(true);
            try {
                await onUpload(record, file);
            } finally {
                setUploading(false);
            }
            return false;
        },
    };

    return (
        <Upload {...uploadProps}>
            <Button size="small" icon={<UploadOutlined />} loading={uploading}>
                {record.hasInvoice ? t("action.replace") : t("action.upload")}
            </Button>
        </Upload>
    );
}

function TransactionPlanTable({
    rows,
    loading,
    error,
    pagination,
    onChange,
    canUploadInvoice,
    currencyOptions = [],
    activeFilters = {},
    canEdit,
    canDelete,
    onViewInvoice,
    onUploadInvoice,
    onEdit,
    onDelete,
    onReload,
}) {
    const { t } = useTranslation();

    const handleUpload = async (record, file) => {
        try {
            await onUploadInvoice(record, file);
        } catch (err) {
            message.error(err?.message || t("invoice.uploadFailed"));
        }
    };

    const columns = useMemo(
        () => [
            {
                title: t("col.id"),
                dataIndex: "id",
                key: "id",
                width: 90,
                fixed: "left",
                sorter: true,
                render: (value) => <span style={{ fontWeight: 600 }}>{value}</span>,
            },
            {
                title: t("col.sender"),
                dataIndex: "sender",
                key: "sender",
                width: 240,
                sorter: true,
                render: (value) => renderCompactText(value, 230),
            },
            {
                title: t("col.beneficiary"),
                dataIndex: "beneficiary",
                key: "beneficiary",
                width: 260,
                sorter: true,
                render: (value) => renderCompactText(value, 250),
            },
            {
                title: t("col.beneficiaryBank"),
                dataIndex: "beneficiaryBank",
                key: "beneficiaryBank",
                width: 200,
                sorter: true,
                render: (value) => renderCompactText(value, 190),
            },
            {
                title: t("col.executionDate"),
                dataIndex: "executionDate",
                key: "executionDate",
                width: 160,
                sorter: true,
                defaultSortOrder: "descend",
                render: (value) => formatPlanDate(value),
            },
            {
                title: t("col.currency"),
                dataIndex: "currency",
                key: "currency",
                width: 120,
                sorter: true,
                filters: currencyOptions.map((item) => ({ text: item, value: item })),
                filteredValue: activeFilters.currency || null,
                filterSearch: true,
                render: (value) => (value ? <Tag color="blue">{value}</Tag> : "-"),
            },
            {
                title: t("col.amount"),
                dataIndex: "amount",
                key: "amount",
                width: 160,
                align: "right",
                sorter: true,
                render: (value) => <span style={{ fontWeight: 600 }}>{formatPlanAmount(value)}</span>,
            },
            {
                title: t("col.invoice"),
                dataIndex: "hasInvoice",
                key: "hasInvoice",
                width: 230,
                fixed: "right",
                filters: invoiceOptions(t).map((item) => ({
                    text: item.label,
                    value: item.value,
                })),
                filteredValue: activeFilters.hasInvoice || null,
                filterMultiple: false,
                render: (_, record) => (
                    <Space wrap size={6}>
                        {record.hasInvoice ? (
                            <Button
                                size="small"
                                type="primary"
                                ghost
                                icon={<EyeOutlined />}
                                onClick={() => onViewInvoice(record)}
                            >
                                {t("action.view")}
                            </Button>
                        ) : (
                            <Tag
                                icon={<ExclamationCircleOutlined />}
                                color="error"
                                style={{ fontWeight: 600, margin: 0 }}
                            >
                                {t("invoice.none")}
                            </Tag>
                        )}

                        {canUploadInvoice && (
                            <InvoiceUploadButton record={record} onUpload={handleUpload} t={t} />
                        )}
                    </Space>
                ),
            },
            ...(canEdit || canDelete
                ? [
                      {
                          title: t("col.actions"),
                          key: "actions",
                          width: 110,
                          fixed: "right",
                          render: (_, record) => (
                              <Space wrap size={6}>
                                  {canEdit && (
                                      <Tooltip title={t("action.edit")}>
                                          <Button
                                              size="small"
                                              aria-label={t("action.edit")}
                                              icon={<EditOutlined />}
                                              onClick={() => onEdit(record)}
                                          />
                                      </Tooltip>
                                  )}

                                  {canDelete && (
                                      <Popconfirm
                                          title={t("delete.confirmTitle")}
                                          description={t("delete.confirmBody")}
                                          okText={t("action.delete")}
                                          cancelText={t("action.cancel")}
                                          okButtonProps={{ danger: true }}
                                          onConfirm={() => onDelete(record)}
                                      >
                                          <Tooltip title={t("action.delete")}>
                                              <Button
                                                  size="small"
                                                  danger
                                                  aria-label={t("action.delete")}
                                                  icon={<DeleteOutlined />}
                                              />
                                          </Tooltip>
                                      </Popconfirm>
                                  )}
                              </Space>
                          ),
                      },
                  ]
                : []),
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [canUploadInvoice, canEdit, canDelete, onViewInvoice, onEdit, onDelete, currencyOptions, activeFilters, t]
    );

    if (error) {
        return (
            <Alert
                type="error"
                showIcon
                message={t("plan.loadError")}
                description={error}
                action={
                    <Button size="small" onClick={onReload}>
                        {t("action.retry")}
                    </Button>
                }
            />
        );
    }

    return (
        <Table
            rowKey="id"
            loading={loading}
            columns={columns}
            dataSource={rows}
            onChange={onChange}
            size="middle"
            scroll={{ x: canEdit || canDelete ? 1450 : 1330 }}
            pagination={pagination}
            locale={{
                emptyText: loading ? (
                    " "
                ) : (
                    <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={t("plan.empty")}
                    />
                ),
            }}
        />
    );
}

export default TransactionPlanTable;
